/**
 * Workspace detection and hub resolution for workspace-aware CLI commands.
 *
 * Hub folders live directly under the resolved "hubs dir" — `wayai-ws/hubs/`
 * on the current layout, or the legacy `workspace/` for un-migrated repos (see
 * `layout.ts` / `resolveLayout`). Each hub folder has a `hub.yaml` (or legacy
 * `wayai.yaml`) file. `WorkspaceInfo.workspaceDir` IS that hubs dir.
 */

import { execFileSync } from 'node:child_process';
import * as fs from 'node:fs';
import * as path from 'node:path';
import * as yaml from 'js-yaml';
import { slugify, UUID_RE } from './utils.js';
import { resolveLayout, hubsDirLabel, isDirectory } from './layout.js';

export interface HubLocation {
  hubFolder: string;
  hubId: string;
  hubEnvironment: string;
}

/** A hub folder where hub.yaml exists but has no hub_id yet (not created on platform). */
export interface NewHubLocation {
  hubFolder: string;
  hubName: string;
  hubType?: string;
}

export interface WorkspaceInfo {
  gitRoot: string;
  workspaceDir: string;
}

/**
 * Find the git repository root. Returns null if not inside a git repo.
 */
export function findGitRoot(): string | null {
  try {
    return execFileSync('git', ['rev-parse', '--show-toplevel'], {
      encoding: 'utf-8',
      stdio: ['pipe', 'pipe', 'pipe'],
    }).trim();
  } catch {
    return null;
  }
}

/**
 * Cheap, non-spawning repo-root finder for hot paths (the update nudge runs on every command).
 * Walks up from cwd looking for a `.git` entry — a directory in a normal clone, a file in a git
 * worktree, both matched by `existsSync`. Unlike `findGitRoot`, this spawns no `git` subprocess
 * (and needs no `git` on PATH). Returns null when not inside a repo.
 */
export function findRepoRootSync(): string | null {
  let current = process.cwd();
  while (true) {
    if (fs.existsSync(path.join(current, '.git'))) return current;
    const parent = path.dirname(current);
    if (parent === current) return null;
    current = parent;
  }
}

/**
 * Detect the hubs directory for the active layout (`wayai-ws/hubs/`, or the
 * legacy `workspace/`). Returns null if not in a git repo or the dir doesn't
 * exist yet (fresh repo before the first pull/init).
 */
export function detectWorkspace(): WorkspaceInfo | null {
  const gitRoot = findGitRoot();
  if (!gitRoot) return null;

  const { hubsDir } = resolveLayout(gitRoot);
  if (!fs.existsSync(hubsDir) || !fs.statSync(hubsDir).isDirectory()) {
    return null;
  }

  return { gitRoot, workspaceDir: hubsDir };
}

/**
 * Resolve the active hubs directory for a workspace-scoped command, exiting with
 * a clear message when not inside a git repo. Callers that want the layout
 * deprecation notice run `warnLayoutOnce(findGitRoot())` separately. Shared by
 * pull/push/diff so the resolution + "Not inside a git repository" exit can't drift.
 */
export function resolveWorkspaceDir(): string {
  const workspace = detectWorkspace();
  if (workspace) return workspace.workspaceDir;

  const gitRoot = findGitRoot();
  if (!gitRoot) {
    console.error('Not inside a git repository.');
    process.exit(1);
  }
  return resolveLayout(gitRoot).hubsDir;
}

/**
 * Read hub metadata from a hub.yaml (or wayai.yaml) file.
 * Returns null if the file doesn't exist or lacks required fields.
 */
function readHubYaml(yamlPath: string): { hubId: string; hubEnvironment: string } | null {
  if (!fs.existsSync(yamlPath)) return null;

  try {
    const content = fs.readFileSync(yamlPath, 'utf-8');
    const config = yaml.load(content) as { hub_id?: string; hub_environment?: string } | undefined;
    if (!config?.hub_id || !config.hub_environment) return null;
    return { hubId: config.hub_id, hubEnvironment: config.hub_environment };
  } catch {
    return null;
  }
}

/** Scan `workspace/<hub>/hub.yaml` for hubs already created on the platform. */
export function scanWorkspaceHubs(workspaceDir: string): HubLocation[] {
  const hubs: HubLocation[] = [];

  for (const top of safeReaddir(workspaceDir)) {
    const topDir = path.join(workspaceDir, top);
    if (!isDirectory(topDir)) continue;

    const meta = readHubYaml(path.join(topDir, 'hub.yaml'))
              || readHubYaml(path.join(topDir, 'wayai.yaml'));
    if (meta) {
      hubs.push({ hubFolder: topDir, hubId: meta.hubId, hubEnvironment: meta.hubEnvironment });
    }
  }

  return hubs;
}

/** A hub folder is editable source only when it's a preview. Production folders
 *  on disk are read-only mirrors (push is blocked for production). */
export function isPreviewHub(hub: HubLocation): boolean {
  return hub.hubEnvironment === 'preview';
}

/**
 * Drop read-only production mirror folders. Apply this at every AUTO-SELECT site
 * (no explicit `--hub`, no enclosing folder) so a preview + its mirror still
 * resolves to the single preview. Explicit selectors and by-id/by-name lookups
 * intentionally bypass this — a production mirror is still directly addressable
 * (e.g. to refresh it).
 */
export function filterPreviewHubs(hubs: HubLocation[]): HubLocation[] {
  return hubs.filter(isPreviewHub);
}

/**
 * Read hub metadata from a hub.yaml that has NO hub_id yet (new hub).
 * Returns the hub name and optional hub_type. Returns null if hub_id is present or hub.name is missing.
 */
function readNewHubYaml(yamlPath: string): { hubName: string; hubType?: string } | null {
  if (!fs.existsSync(yamlPath)) return null;

  try {
    const content = fs.readFileSync(yamlPath, 'utf-8');
    const config = yaml.load(content) as { hub_id?: string; hub?: { name?: string; hub_type?: string } } | undefined;
    // Skip if hub_id is present (already created on platform)
    if (config?.hub_id) return null;
    // Must have hub.name to be a valid new hub
    if (!config?.hub?.name) return null;
    return { hubName: config.hub.name, hubType: config.hub.hub_type };
  } catch {
    return null;
  }
}

/** Scan `workspace/<hub>/hub.yaml` for hubs not yet created on the platform (no `hub_id`). */
export function scanNewHubs(workspaceDir: string): NewHubLocation[] {
  const hubs: NewHubLocation[] = [];

  for (const top of safeReaddir(workspaceDir)) {
    const topDir = path.join(workspaceDir, top);
    if (!isDirectory(topDir)) continue;

    const meta = readNewHubYaml(path.join(topDir, 'hub.yaml'))
              || readNewHubYaml(path.join(topDir, 'wayai.yaml'));
    if (meta) {
      hubs.push({ hubFolder: topDir, hubName: meta.hubName, hubType: meta.hubType });
    }
  }

  return hubs;
}

/**
 * Compute the hub folder slug with preview disambiguation.
 * Production: slugify(hubName)
 * Preview: slugify(hubName) + "--" + slugify(previewLabel || branchName || hubId[0:8])
 *
 * The double-hyphen join is unambiguous: slugifyKebab collapses internal hyphen
 * runs, so neither the base nor the suffix can contain "--".
 *
 * IMPORTANT: This must produce identical output to the backend's computeHubSlug
 * (apps/backend/src/routes/api/ci/lookup.ts) and getHubFolderSlug
 * (apps/backend/src/routes/api/files/index.ts). All three now resolve to the
 * SAME canonical implementation: `slugify` here is a re-export of @wayai/core's
 * `slugifySkillName` (= `slugifyKebab(name, 64)`), which the backend imports
 * directly — no separate copy to keep in lockstep.
 */
export function getHubFolderSlug(
  hubName: string,
  hubEnvironment: string,
  hubId: string,
  previewLabel?: string | null,
  branchName?: string | null
): string {
  const base = slugify(hubName);
  if (hubEnvironment !== 'preview') return base;
  const suffix = previewLabel || branchName || hubId.slice(0, 8);
  return `${base}--${slugify(suffix)}`;
}

/**
 * Resolve a hub folder in the workspace by hub_id match (handles folder renames),
 * falling back to a flat slugified hub path with preview disambiguation.
 */
export function resolveHubFolder(
  workspaceDir: string,
  hubId: string | undefined,
  hubName: string,
  hubEnvironment: string,
  previewLabel?: string | null,
  branchName?: string | null
): string {
  // First: try to find by hub_id match (handles folder renames)
  if (hubId) {
    const allHubs = scanWorkspaceHubs(workspaceDir);
    const match = allHubs.find((h) => h.hubId === hubId);
    if (match) return match.hubFolder;
  }

  // Fallback: flat slugified path with preview disambiguation
  const hubFolderSlug = getHubFolderSlug(hubName, hubEnvironment, hubId || '', previewLabel, branchName);
  return path.join(workspaceDir, hubFolderSlug);
}

/**
 * Resolve the on-disk folder for a known hub_id by scanning the workspace.
 * Returns null when no workspace exists or the hub isn't materialized locally.
 */
export function findLocalHubFolder(hubId: string): string | null {
  const workspace = detectWorkspace();
  const gitRoot = findGitRoot();
  const workspaceDir = workspace?.workspaceDir ?? (gitRoot ? resolveLayout(gitRoot).hubsDir : null);
  if (!workspaceDir || !fs.existsSync(workspaceDir)) return null;
  const match = scanWorkspaceHubs(workspaceDir).find((h) => h.hubId === hubId);
  return match ? match.hubFolder : null;
}

/**
 * Get hub folders with uncommitted changes via `git status`.
 * Filters to hub.yaml, wayai.yaml, and agents/*.yaml/md files under workspace/.
 * Read-only production mirror folders are excluded — a GitOps `wayai push` over
 * changed folders must never attempt a production hub (push is blocked anyway,
 * but this avoids a noisy CI failure on an edited/committed mirror).
 */
export function getChangedHubs(workspaceDir: string): HubLocation[] {
  const gitRoot = findGitRoot();
  if (!gitRoot) return [];

  // Get workspace path relative to git root for the git status query
  const relWorkspace = path.relative(gitRoot, workspaceDir);

  // Ensure the workspace is inside the git root (no path traversal)
  const normalizedRel = path.normalize(relWorkspace);
  if (normalizedRel.startsWith('..') || path.isAbsolute(normalizedRel)) {
    return [];
  }

  let statusOutput: string;
  try {
    statusOutput = execFileSync('git', ['status', '--porcelain', '-u', '--', `${relWorkspace}/`], {
      encoding: 'utf-8',
      cwd: gitRoot,
      stdio: ['pipe', 'pipe', 'pipe'],
    });
  } catch {
    return [];
  }

  // Split into lines, preserving leading spaces (porcelain status prefix uses them)
  const lines = statusOutput.split('\n').filter((l) => l.length > 0);

  // Extract file paths from porcelain output (skip the 2-char status + space prefix)
  const changedFiles = lines.map((line) => {
    // Porcelain format: "XY file" or "XY orig -> file" (for renames)
    const filePart = line.slice(3);
    // Handle renames: "orig -> dest"
    const arrowIdx = filePart.indexOf(' -> ');
    return arrowIdx >= 0 ? filePart.slice(arrowIdx + 4) : filePart;
  });

  // Filter to hub config files (hub.yaml, wayai.yaml, agents/*.yaml, agents/*.md)
  const hubFiles = changedFiles.filter((file) => {
    return file.endsWith('/hub.yaml') || file.endsWith('hub.yaml')
        || file.endsWith('/wayai.yaml') || file.endsWith('wayai.yaml')
        || /\/agents\/[^/]+\.(md|yaml)$/.test(file);
  });

  if (hubFiles.length === 0) return [];

  // Extract unique hub folder paths (absolute)
  const hubFolders = new Set<string>();

  for (const file of hubFiles) {
    const absFile = path.resolve(gitRoot, file);
    if (file.endsWith('hub.yaml') || file.endsWith('wayai.yaml')) {
      hubFolders.add(path.dirname(absFile));
    } else if (file.includes('/agents/')) {
      const agentsIndex = absFile.lastIndexOf('/agents/');
      hubFolders.add(absFile.substring(0, agentsIndex));
    }
  }

  // Read each hub folder's hub.yaml (or wayai.yaml)
  const hubs: HubLocation[] = [];

  for (const hubFolder of hubFolders) {
    const meta = readHubYaml(path.join(hubFolder, 'hub.yaml'))
              || readHubYaml(path.join(hubFolder, 'wayai.yaml'));
    if (meta) {
      hubs.push({ hubFolder, hubId: meta.hubId, hubEnvironment: meta.hubEnvironment });
    }
  }

  return filterPreviewHubs(hubs);
}

export interface ResolvedHubPath {
  hubId: string;
  hubFolder: string;
  hubEnvironment: string;
}

export interface UnresolvedSegments {
  orgPrefix?: string;
  hub: string;
}

/**
 * Parse a hub path (hub, or org/hub), resolve it locally if possible, and
 * return the parsed segments for API fallback if local resolution fails.
 *
 * Returns `{ resolved }` if found locally, or `{ segments }` for API lookup.
 */
export function resolveExplicitHubPath(
  hubPath: string
): { resolved: ResolvedHubPath } | { segments: UnresolvedSegments } {
  const parts = hubPath.split('/');

  let hubSegment: string;
  let orgPrefix: string | undefined;

  if (parts.length === 1) {
    hubSegment = parts[0];
  } else if (parts.length === 2) {
    [orgPrefix, hubSegment] = parts;
  } else {
    throw new Error('Hub path must be hub or org/hub');
  }

  // Try local workspace resolution first (avoids API call, handles slugified folder names)
  const workspace = detectWorkspace();
  if (workspace) {
    const local = findHubByFolderName(workspace.workspaceDir, hubSegment);
    if (local) {
      return {
        resolved: {
          hubId: local.hubId,
          hubFolder: local.hubFolder,
          hubEnvironment: local.hubEnvironment,
        },
      };
    }
  }

  return { segments: { orgPrefix, hub: hubSegment } };
}

/** Find a hub in `workspace/<hub>/` whose folder basename matches the given name. */
export function findHubByFolderName(
  workspaceDir: string,
  hubFolderName: string,
): HubLocation | null {
  const all = scanWorkspaceHubs(workspaceDir);
  return all.find((h) => path.basename(h.hubFolder) === hubFolderName) ?? null;
}

/**
 * Resolve a hub SELECTOR (the `--from` arg of `wayai admin template freeze`) to an
 * absolute hub folder under `hubsDir`. Accepts, in order: an explicit existing dir path
 * (one holding a `hub.yaml`), a hub UUID, or a hub-folder basename. Returns null when
 * nothing resolves — the caller lists the available hubs.
 *
 * Resolves by hub_id / name, never by trusting a stale id-suffixed folder literal, so a
 * recreated hub (new instance id → new folder suffix) still resolves (gh #2960). The
 * `wayai use` binding fallback (no selector) stays in the caller — it needs `readBinding`,
 * which is git-state-dependent and kept out of this pure-fs resolver for testability.
 */
export function resolveHubFolderBySelector(hubsDir: string, selector: string): string | null {
  // 1) An explicit dir path (absolute or relative to cwd) that holds a hub.yaml.
  const asPath = path.resolve(selector);
  if (isDirectory(asPath) && fs.existsSync(path.join(asPath, 'hub.yaml'))) return asPath;
  // 2) A hub UUID → scan by hub_id (tolerant of the folder rename).
  if (UUID_RE.test(selector)) {
    const match = scanWorkspaceHubs(hubsDir).find((h) => h.hubId === selector);
    return match ? match.hubFolder : null;
  }
  // 3) A folder basename.
  const byName = findHubByFolderName(hubsDir, selector);
  return byName ? byName.hubFolder : null;
}

/**
 * Auto-rename a hub folder when the hub name has changed.
 * Compares current folder name against the expected slug and renames if different.
 * Returns the new folder path (or same path if no rename occurred).
 */
export function autoRenameHubFolder(
  currentFolder: string,
  hubName: string,
  hubEnvironment: string,
  hubId: string,
  previewLabel?: string | null,
  branchName?: string | null
): string {
  const expectedSlug = getHubFolderSlug(hubName, hubEnvironment, hubId, previewLabel, branchName);
  const currentSlug = path.basename(currentFolder);

  if (currentSlug === expectedSlug) return currentFolder;

  const targetFolder = path.join(path.dirname(currentFolder), expectedSlug);

  if (fs.existsSync(targetFolder)) {
    console.warn(`  Warning: skipping hub folder rename ${currentSlug} → ${expectedSlug} (target already exists)`);
    return currentFolder;
  }

  try {
    fs.renameSync(currentFolder, targetFolder);
  } catch (err) {
    console.warn(`  Warning: could not rename ${currentSlug} → ${expectedSlug}: ${err instanceof Error ? err.message : String(err)}`);
    return currentFolder;
  }
  console.log(`  Renamed hub folder ${currentSlug} → ${expectedSlug}`);
  console.log(`  Note: stage this rename in git before committing (e.g. git add -A)`);
  return targetFolder;
}

/**
 * Walk from `process.cwd()` up to (and including) `workspaceDir`, returning
 * the first directory whose `hub.yaml` (or legacy `wayai.yaml`) carries a
 * valid `hub_id`/`hub_environment`. Returns null if no enclosing hub folder
 * is found before reaching the workspace root.
 */
export function findEnclosingHubFolder(workspaceDir: string): HubLocation | null {
  let current = process.cwd();
  const stop = path.resolve(workspaceDir);

  while (true) {
    const meta = readHubYaml(path.join(current, 'hub.yaml'))
              || readHubYaml(path.join(current, 'wayai.yaml'));
    if (meta) {
      return { hubFolder: current, hubId: meta.hubId, hubEnvironment: meta.hubEnvironment };
    }
    if (current === stop) return null;
    const parent = path.dirname(current);
    if (parent === current) return null;
    current = parent;
  }
}

/**
 * Resolve the active hub for a workspace-scoped command.
 *
 * Resolution order:
 *   1. `--hub <uuid|folder-name>` flag in args (and consumed from args[])
 *   2. cwd is inside a hub folder
 *   3. exactly one hub in workspace/
 *   4. otherwise: error and exit
 *
 * Returns the hub's `hub_id`. Mutates `args` to remove the consumed `--hub` pair.
 */
export function resolveActiveHubId(args: string[]): string {
  // Parse and remove --hub <value> from args
  let selector: string | undefined;
  const hubIdx = args.indexOf('--hub');
  if (hubIdx !== -1) {
    const next = args[hubIdx + 1];
    if (!next || next.startsWith('--')) {
      console.error('--hub requires a value (uuid or workspace folder name).');
      process.exit(1);
    }
    selector = next;
    args.splice(hubIdx, 2);
  }

  const workspace = detectWorkspace();
  const gitRoot = findGitRoot();
  const workspaceDir = workspace?.workspaceDir ?? (gitRoot ? resolveLayout(gitRoot).hubsDir : null);
  // Display label for messages: e.g. "wayai-ws/hubs" (new) or "workspace" (legacy).
  const wsLabel = hubsDirLabel(gitRoot);

  if (!workspaceDir || !fs.existsSync(workspaceDir)) {
    if (selector && UUID_RE.test(selector)) return selector;
    console.error(`No ${wsLabel}/ directory found. Run \`wayai pull --hub <uuid>\` to fetch a hub, or pass --hub <uuid>.`);
    process.exit(1);
  }

  const allHubs = scanWorkspaceHubs(workspaceDir);

  if (selector) {
    const match = allHubs.find((h) =>
      h.hubId === selector || path.basename(h.hubFolder) === selector,
    );
    if (match) return match.hubId;

    // Allow raw UUID even when not materialized locally — useful for read-only commands.
    if (UUID_RE.test(selector)) return selector;

    console.error(`No hub matching --hub ${selector} found in ${wsLabel}/.`);
    process.exit(1);
  }

  const enclosing = findEnclosingHubFolder(workspaceDir);
  if (enclosing) return enclosing.hubId;

  // Auto-select ignores read-only production mirror folders so a preview + its
  // mirror still resolves to the single preview.
  const previewHubs = filterPreviewHubs(allHubs);

  if (previewHubs.length === 1) return previewHubs[0].hubId;

  if (previewHubs.length === 0) {
    console.error(`No hub folders found in ${wsLabel}/. Run \`wayai pull --hub <uuid>\` to fetch a hub for the first time, or pass --hub <uuid>.`);
    process.exit(1);
  }

  console.error(`Multiple hubs in ${wsLabel}/. Pass --hub <uuid|folder-name> or run from inside a hub folder:`);
  for (const h of previewHubs) {
    console.error(`  ${path.basename(h.hubFolder)} (${h.hubId})`);
  }
  process.exit(1);
}

/**
 * The `hub_environment` of a locally-materialized hub folder (`preview` |
 * `production`), or null if the hub isn't in the workspace. Read from the folder's
 * `hub.yaml` — works for read-only production mirrors too. Callers that need the
 * environment for a hub that may not be local should fall back to the server.
 */
export function localHubEnvironment(hubId: string): string | null {
  const workspace = detectWorkspace();
  const gitRoot = findGitRoot();
  const workspaceDir = workspace?.workspaceDir ?? (gitRoot ? resolveLayout(gitRoot).hubsDir : null);
  if (!workspaceDir || !fs.existsSync(workspaceDir)) return null;
  return scanWorkspaceHubs(workspaceDir).find((h) => h.hubId === hubId)?.hubEnvironment ?? null;
}

function safeReaddir(dir: string): string[] {
  try {
    return fs.readdirSync(dir).filter((entry) => !entry.startsWith('.'));
  } catch {
    return [];
  }
}
